import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, ClipboardPlus, FileText, Gauge, ListChecks, Zap } from "lucide-react";
import { api } from "../lib/api";
import { fmtDate, hasRole, NOSTROMO_ROLE, VIG_REPORT_ROLES, VIG_SUPERVISOR_ROLE } from "../lib/roles";
import { usePerm } from "../context/PermContext";
import { Empty, Loading, PageHeader, Panel } from "../components/common";
import { ReportCalendar, VIG_TAB } from "../components/ReportCalendar";
import { VigMineList, VigReportsList } from "./Vigilanza";

const Action = ({ to, icon: Icon, label, testid }) => (
  <Link to={to} className="panel panel-hover !p-5 flex items-center gap-4" data-testid={testid}>
    <span className="w-10 h-10 rounded-xl bg-[var(--teal-soft)] flex items-center justify-center"><Icon className="w-5 h-5 text-[var(--teal)]" /></span>
    <span className="font-semibold text-[var(--navy)]">{label}</span>
  </Link>
);

export default function Report() {
  const { viewUser: user } = usePerm();
  const [reports, setReports] = useState(null);
  useEffect(() => { api("/reports").then((d) => setReports(d.reports || [])).catch(() => setReports([])); }, []);
  const { can, mySettori, settori, admin } = usePerm();
  const isVig = hasRole(user, VIG_REPORT_ROLES);
  const vigHead = admin || hasRole(user, [VIG_SUPERVISOR_ROLE]);
  const shiftRole = mySettori.length > 0;
  const isNostromo = hasRole(user, [NOSTROMO_ROLE]);
  const view = can("report.view");
  const edit = can("report.edit");
  const calSettori = [...settori.filter((s) => !VIG_REPORT_ROLES.includes(s)), ...(vigHead ? [VIG_TAB] : [])];

  return (
    <div>
      <PageHeader subtitle="Report" title="Report di lavoro" />
      {view && calSettori.length > 0 && <ReportCalendar settori={calSettori} />}
      {view && settori.length > mySettori.length && (
        <div className="grid sm:grid-cols-2 gap-3 mb-10 stagger">
          <Action to="/report/turni" icon={ClipboardList} label="Report di fine turno di tutti i profili" testid="all-shift-reports-link" />
        </div>
      )}
      {shiftRole && (edit || view) && (
        <div className="grid sm:grid-cols-2 gap-3 mb-10 stagger">
          {edit && <Action to="/report/shift-new" icon={ClipboardPlus} label={isNostromo ? "Invia report giornaliero" : "Invia report di fine turno"} testid="shift-report-new-link" />}
          {view && <Action to="/report/mine" icon={ListChecks} label="I miei report" testid="shift-report-mine-link" />}
        </div>
      )}
      {isVig && (edit || view) && (
        <div className="grid sm:grid-cols-2 gap-3 mb-10 stagger">
          {edit && <Action to="/vigilanza/new" icon={ClipboardPlus} label="Report turno Vigilanza" testid="vig-report-new-link" />}
          {view && <Action to="/vigilanza/mine" icon={ListChecks} label="I miei report Vigilanza" testid="vig-report-mine-link" />}
        </div>
      )}
      {isVig ? (view && <>
        <h2 className="font-display text-xl font-bold text-[var(--navy)] mb-4">Report Vigilanza</h2>
        <div data-testid="report-vig-section">{vigHead ? <VigReportsList /> : <VigMineList />}</div>
      </>) : <>
      <h2 className="font-display text-xl font-bold text-[var(--navy)] mb-4">Report inviati</h2>
      {!reports ? <Loading /> : reports.length === 0 ? <Empty title="Nessun report inviato" text="Crea il primo report per la tua mansione." /> : (
        <div className="space-y-3 stagger" data-testid="reports-list">{reports.map((r) => (
          <Link key={r.id} to={`/report/view/${r.id}`} className="block" data-testid={`report-${r.id}`}>
            <Panel className="panel-hover !p-5 flex gap-4">
              <FileText className="w-5 h-5 text-[var(--teal)] shrink-0 mt-0.5" />
              <div className="min-w-0"><div className="flex flex-wrap gap-2 items-center"><span className="font-semibold text-[var(--navy)]">{r.titolo}</span><span className="tag tag-teal">{r.tipo}</span></div>
                <p className="text-sm text-slate-600 mt-1 line-clamp-2">{r.contenuto}</p>
                <p className="text-xs text-slate-400 mt-2">{r.author_name} · {r.mansione} · {fmtDate(r.created_at, true)}</p></div>
            </Panel>
          </Link>))}
        </div>)}
      </>}
      {(can("letture.ee") || can("letture.gas")) && (
        <>
        <h2 className="font-display text-xl font-bold text-[var(--navy)] mt-10 mb-4">Letture contatori</h2>
        <div className="grid sm:grid-cols-2 gap-3 stagger" data-testid="meters-section">
          {can("letture.ee") && <Action to="/report/ee" icon={Zap} label="Letture EE pontili" testid="report-ee-link" />}
          {can("letture.gas") && <Action to="/report/immobili" icon={Gauge} label="Letture EE/GAS Immobili" testid="report-immobili-link" />}
        </div>
        </>
      )}
    </div>
  );
}
